/** Purchasing selection owns a transient user overlay, never source writes.
 * Inputs: the reviewed offers and their existing conservative comparison rows.
 * Outputs: suggestions, supplier groups and inert CSVs with original evidence.
 * Tests: offerSelection.spec.ts (model and browser lifecycle).
 */
import { offerComparison, type ComparisonRow, type ReviewLine, type ReviewOffer } from "./offerComparison";
import { toNumber } from "./format";
import { offerCsv } from "./offerCsv";

export type PriceRanking = { amounts: number[]; lowest: number; winners: number[] };
export type PurchaseChoice = { offerId: string | null; method: "manual" | "suggested" };
export type PurchaseChoices = Record<string, PurchaseChoice>;
export type SelectedPosition = { key: string; offer: ReviewOffer; line: ReviewLine; method: PurchaseChoice["method"]; cents: number };
export type SupplierSelection = { key: string; supplier: string; currency: string; positions: SelectedPosition[]; cents: number };

export function rankOfferRow(row: ComparisonRow): PriceRanking | null {
  return row.lines.some(line => line?.purchase_selection_allowed === false) ? null : rankOfferPrices(row);
}

export function rankOfferPrices(row: ComparisonRow): PriceRanking | null {
  if (!row.common) return null;
  const amounts: number[] = [];
  for (const line of row.lines) {
    const price = toNumber(line?.net_unit_price);
    const qty = toNumber(line?.qty);
    const amount = toNumber(line?.line_net_amount);
    if (!line || line.comparison_warning || price === null || price <= 0 || qty === null || qty <= 0 || amount === null || amount <= 0) return null;
    const cents = Math.round(amount * 100);
    if (!Number.isSafeInteger(cents) || cents <= 0) return null;
    amounts.push(cents);
  }
  const lowest = Math.min(...amounts);
  return { amounts, lowest, winners: amounts.flatMap((amount, index) => amount === lowest ? [index] : []) };
}

export function offerSelectionScope(offers: ReviewOffer[]): string {
  // Include all displayed source fields, not only IDs: refreshed source amounts,
  // reordered offers or a changed reference basis must not retain old choices.
  return JSON.stringify(offers.map(offer => [offer.id, offer.docId, offer.name, offer.preview]));
}

export function suggestPurchaseChoices(offers: ReviewOffer[], choices: PurchaseChoices): PurchaseChoices {
  const next = { ...choices };
  for (const row of offerComparison(offers)) {
    const ranking = rankOfferRow(row);
    if (ranking?.winners.length === 1 && !Object.hasOwn(next, row.key)) {
      next[row.key] = { offerId: offers[ranking.winners[0]].id, method: "suggested" };
    }
  }
  return next;
}

export function purchaseSelection(offers: ReviewOffer[], choices: PurchaseChoices) {
  const rows = offerComparison(offers);
  const groups = new Map<string, SupplierSelection>();
  const selected: SelectedPosition[] = [];
  const eligible = rows.filter(row => rankOfferRow(row));
  for (const row of eligible) {
    const choice = choices[row.key];
    const index = offers.findIndex(offer => offer.id === choice?.offerId);
    const ranking = rankOfferRow(row)!;
    if (index < 0 || !row.lines[index]) continue;
    const offer = offers[index];
    const line = row.lines[index]!;
    const position = { key: row.key, offer, line, method: choice.method, cents: ranking.amounts[index] };
    const supplier = offer.preview.supplier;
    const currency = offer.preview.currency!;
    const key = JSON.stringify([supplier, currency]);
    const group = groups.get(key) ?? { key, supplier, currency, positions: [], cents: 0 };
    group.positions.push(position); group.cents += position.cents;
    groups.set(key, group); selected.push(position);
  }
  const remaining = eligible.length - selected.length;
  const selectedKeys = new Set(selected.map(position => position.key));
  const remainingTies = eligible.filter(row => !selectedKeys.has(row.key) && rankOfferRow(row)!.winners.length > 1).length;
  const incomparable = rows.length - eligible.length;
  return { groups: [...groups.values()], selected, remaining, remainingTies, incomparable, totalRows: rows.length, partial: remaining > 0 || incomparable > 0 };
}

export function purchaseSelectionCsv(offers: ReviewOffer[], choices: PurchaseChoices, groupKey: string): string | null {
  // Revalidate current scope for every download; no arbitrary supplied rows.
  const selection = purchaseSelection(offers, choices);
  const group = selection.groups.find(value => value.key === groupKey);
  if (!group?.positions.length || !Number.isSafeInteger(group.cents)) return null;
  const rows: unknown[][] = [["Dokumentart", "Umfang", "Noch zu wählen", "Nicht vergleichbare Zeilen", "Lieferant", "Währung", "Originalangebot", "Angebotsdatum", "Quelldatei", "Position", "PDF-Seite", "Artikelnummer", "Herstellerartikelnummer", "Beschreibung", "Menge", "Einheit", "Angebotspreis netto", "Positionssumme netto", "Auswahl", "Hinweis"]];
  for (const { offer, line, method } of group.positions) rows.push([
    "Einkaufsauswahl – keine Bestellung", selection.partial ? "Teilauswahl" : "Alle vergleichbaren Positionen gewählt", selection.remaining, selection.incomparable,
    group.supplier, group.currency, offer.preview.doc_number, offer.preview.doc_date, offer.name, line.line_pos, line.page_no,
    line.article_code, line.manufacturer_article_code, line.description, line.qty, line.uom, line.net_unit_price, line.line_net_amount,
    method === "manual" ? "Manuell gewählt" : "Automatisch gewählt", "Gültigkeit, Lieferumfang, Fracht und Bedingungen der Originalangebote vor Bestellung bestätigen.",
  ]);
  return offerCsv(rows);
}
